import httpService, { HttpError } from '../../../core/services/HttpService';
import { Employee } from '../../../hooks/useEmployeeData';
import { OnboardingStatus } from '../../../logic/onboardingSteps';

/**
 * Interface for status option object structure (used by getOnboardingStatusOptions)
 */
export interface StatusOption {
  value: string;
  label: string;
}

/**
 * Type for the onboarding status constants object
 */
export interface OnboardingStatusConstants {
  readonly INVITED: 'INVITED';
  readonly WAITING_FOR_MANAGER: 'WAITING_FOR_MANAGER';
  readonly ACTIVE: 'ACTIVE';
  readonly DEACTIVATED: 'DEACTIVATED';
  readonly PENDING_DEACTIVATION: 'PENDING_DEACTIVATION';
}

/**
 * Interface for work area information in overview
 */
export interface WorkAreaInfo {
  id: number;
  name: string;
  is_primary: boolean;
}

/**
 * Interface for manager contact information
 */
export interface ManagerContact {
  id?: number;
  employee_id?: number;
  user_id?: number;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  display_name?: string;
  email: string;
  phone?: string;
}

/**
 * Interface for managers by work area
 */
export interface ManagersByWorkArea {
  work_area_id: number;
  work_area_name: string;
  managers: ManagerContact[];
}

/**
 * Interface for managers information
 */
export interface ManagersInfo {
  primary?: ManagerContact;
  by_work_area?: ManagersByWorkArea[];
}

/**
 * Interface for onboarding milestone
 */
export interface OnboardingMilestone {
  step: string;
  label: string;
  status: 'PENDING' | 'DONE' | 'SKIPPED';
  completed_at?: string;
}

/**
 * Interface for shift information
 */
export interface ShiftInfo {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  work_area_name: string;
}

/**
 * Interface for time tracking information
 */
export interface TimeTrackingInfo {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  work_area_name: string;
  hours?: number;
}

/**
 * Interface for leave balance
 */
export interface LeaveBalance {
  available: number;
  total: number;
  used: number;
  contractual_per_year?: number;
  entitlement_current_year?: number;
  statutory_days?: number;
  extra_days?: number;
  prorata_months?: number;
  is_explicit?: boolean;
}

/**
 * Interface for absence counts
 */
export interface AbsenceCounts {
  requested: number;
  approved: number;
  rejected: number;
}

/**
 * Interface for absence information
 */
export interface AbsenceInfo {
  id: number;
  start_date: string;
  end_date: string;
  absence_type: string;
  status: string;
  days: number;
}

/**
 * Interface for employee overview response
 */
export interface EmployeeOverview {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  photo_url?: string;
  birth_date?: string;
  start_date?: string;
  onboarding_status: string;
  work_areas: WorkAreaInfo[];
  primary_work_area?: { id: number; name: string };
  primary_work_area_manager?: ManagerContact;
  managers?: ManagersInfo;
  onboarding_milestones?: OnboardingMilestone[];
  leave_balance?: LeaveBalance;
  absence_counts?: AbsenceCounts;
  upcoming_absences?: AbsenceInfo[];
  next_shifts?: ShiftInfo[];
  last_time_trackings?: TimeTrackingInfo[];
  key_facts?: {
    employment_type?: string;
    contract_end_date?: string;
    weekly_hours?: number;
  };
  deactivated_at?: string;
}

/**
 * Interface for person information in employee profile
 */
export interface ProfilePerson {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  birth_date?: string;
  birth_place?: string;
  nationality?: string;
  gender?: string;
  street?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
}

/**
 * Interface for job information in employee profile
 */
export interface ProfileJob {
  employment_type?: string;
  start_date?: string;
  contract_end_date?: string;
  position_title?: string;
  work_areas?: number[];
  primary_work_area?: number;
}

/**
 * Interface for payroll information in employee profile
 */
export interface ProfilePayroll {
  hourly_wage?: number;
  monthly_salary?: number;
  compensation_type?: 'HOURLY' | 'MONTHLY';
  health_insurance_type?: string;
  health_insurance_name?: string;
  is_family_insured?: boolean;
  lohnsteuer_klasse?: string;
  religion?: string;
  sozialversicherungsnummer?: string;
  steuer_id?: string;
}

/**
 * Interface for working pattern in employee profile
 */
export interface ProfileWorkingPattern {
  contract_hours_per_week?: string;
  work_pattern_mode?: string;
  regular_workdays?: number[]; // Array [1,1,1,1,1,0,0]
  hours_per_week?: number; // Legacy field
  days_distribution?: number[]; // Legacy field
}

/**
 * Interface for leave/holiday information in employee profile
 */
export interface ProfileLeave {
  holiday_days_per_year?: string | null; // Decimal string e.g. "28.00"
  holiday_extra_days_per_year?: string | null; // Decimal string e.g. "5.00"
  holiday_opening_balance_days?: string | null; // Decimal string e.g. "2.50"
  holiday_balance_effective_year?: number | null; // Year e.g. 2025
}

/**
 * Interface for employee profile data
 */
export interface EmployeeProfile {
  id: number;
  person?: ProfilePerson;
  job?: ProfileJob;
  payroll?: ProfilePayroll;
  working_pattern?: ProfileWorkingPattern;
  leave?: ProfileLeave;
  clothing_size?: string | null;
  clothing_cut?: string | null;
  has_received_uniform?: boolean | null;
}

/**
 * Employee Service
 * Centralized service for employee-related API operations
 */
class EmployeeService {
  static readonly ONBOARDING_STATUS: OnboardingStatusConstants = {
    INVITED: 'INVITED',
    WAITING_FOR_MANAGER: 'WAITING_FOR_MANAGER',
    ACTIVE: 'ACTIVE',
    DEACTIVATED: 'DEACTIVATED',
    PENDING_DEACTIVATION: 'PENDING_DEACTIVATION',
  } as const;

  /**
   * Fetch all employees from the admin endpoint
   * @returns Promise that resolves to the employees array
   */
  static async fetchAllEmployees(): Promise<Employee[]> {
    try {
      const allStatuses = Object.values(this.ONBOARDING_STATUS).join(',');
      const response = await httpService.get<Employee[] | { results: Employee[] }>(`/api/admin/employees/?onboarding_status=${allStatuses}`);
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
        return data.results;
      }
      return [];
    } catch (e:any) {
      if ((e as HttpError)?.status === 404) {
        const resp = await httpService.get<Employee[] | { results: Employee[] }>('/api/employees/');
        const data = resp.data;
        if (Array.isArray(data)) {
          return data;
        }
        if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      }
      throw e;
    }
  }

  /**
   * Fetch employee overview data by ID
   * @param employeeId - ID of the employee
   * @returns Promise that resolves to the employee overview
   */
  static async getEmployeeOverview(employeeId: number): Promise<EmployeeOverview> {
    const response = await httpService.get<EmployeeOverview>(`/api/employees/${employeeId}/overview/`);
    return response.data;
  }

  /**
   * Fetch employee profile data by ID
   * @param employeeId - ID of the employee
   * @returns Promise that resolves to the employee profile
   */
  static async getEmployeeProfile(employeeId: number): Promise<EmployeeProfile> {
    const response = await httpService.get<EmployeeProfile>(`/api/employees/${employeeId}/profile/`);
    return response.data;
  }

  /**
   * Update employee profile data
   * @param employeeId - ID of the employee
   * @param profileData - Partial profile data to update
   * @returns Promise that resolves to the updated profile
   */
  static async updateEmployeeProfile(employeeId: number, profileData: Partial<EmployeeProfile>): Promise<EmployeeProfile> {
    const response = await httpService.patch<EmployeeProfile>(`/api/employees/${employeeId}/profile/`, profileData);
    return response.data;
  }

  /**
   * Fetch employees filtered by onboarding status
   * @param statusFilters - Array of onboarding statuses to filter by
   * @returns Promise that resolves to the filtered employees array
   */
  static async fetchEmployeesByStatus(statusFilters: string[] | null | undefined): Promise<Employee[]> {
    const params = new URLSearchParams();
    if (statusFilters && statusFilters.length > 0) {
      params.append('onboarding_status', statusFilters.join(','));
    }

    try {
      const url = `/api/admin/employees/${params.toString() ? '?' + params.toString() : ''}`;
      const response = await httpService.get<Employee[] | { results: Employee[] }>(url);
      const data = response.data;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
        return data.results;
      }
      return [];
    } catch (e:any) {
      if ((e as HttpError)?.status === 404) {
        const url = `/api/employees/${params.toString() ? '?' + params.toString() : ''}`;
        const response = await httpService.get<Employee[] | { results: Employee[] }>(url);
        const data = response.data;
        if (Array.isArray(data)) {
          return data;
        }
        if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
          return data.results;
        }
        return [];
      }
      throw e;
    }
  }

  /**
   * Update employee information
   * @param employeeId - ID of the employee to update
   * @param updateData - Partial employee data to update
   * @returns Promise that resolves to the updated employee
   */
  static async updateEmployee(employeeId: number, updateData: Partial<Employee>): Promise<Employee> {
    const response = await httpService.patch<Employee>(`/api/employees/${employeeId}/`, updateData);
    return response.data;
  }

  /**
   * Update employee information via admin endpoint
   * @param employeeId - ID of the employee to update
   * @param updateData - Partial employee data to update
   * @returns Promise that resolves to the updated employee
   */
  static async updateEmployeeAsAdmin(employeeId: number, updateData: Partial<Employee>): Promise<Employee> {
    // Alias für Admin‑Update – gleicher Fallback
    return this.updateEmployee(employeeId, updateData);
  }

  /**
   * Create a new employee invitation
   * @param invitationData - Data for the employee invitation
   * @returns Promise that resolves to the created invitation
   */
  static async createEmployeeInvitation(invitationData: any): Promise<any> {
    const response = await httpService.post<any>('/api/employee-invitations/', invitationData);
    return response.data;
  }

  /**
   * Get all available onboarding status options
   * @returns Array of status options with value and label
   */
  static getOnboardingStatusOptions(): StatusOption[] {
    return [
      { value: '', label: 'All Statuses' },
      { value: this.ONBOARDING_STATUS.INVITED, label: 'Invited' },
      { value: this.ONBOARDING_STATUS.WAITING_FOR_MANAGER, label: 'Waiting for Manager' },
      { value: this.ONBOARDING_STATUS.ACTIVE, label: 'Active' },
      { value: this.ONBOARDING_STATUS.PENDING_DEACTIVATION, label: 'Pending Deactivation' },
      { value: this.ONBOARDING_STATUS.DEACTIVATED, label: 'Deactivated' },
    ];
  }

  /**
   * Bulk import DATEV personnel numbers for multiple employees
   * @param rows - Array of employee_id and personalnr pairs
   * @returns Promise that resolves to the import result
   */
  static async bulkImportDatevPersonalnr(rows: Array<{ employee_id: number; personalnr: string }>): Promise<any> {
    try {
      const response = await httpService.post<any>('/api/employees/datev-personalnr/import', { rows });
      return response.data;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        const response = await httpService.post<any>('/api/employees/datev-personalnr/import/', { rows });
        return response.data;
      }
      throw e;
    }
  }

  /**
   * Upload employee photo
   * @param employeeId - ID of the employee
   * @param file - Image file to upload
   * @returns Promise that resolves to the upload result with photo URL
   */
  static async uploadEmployeePhoto(employeeId: number, file: File): Promise<{ photo_url: string }> {
    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await httpService.post<{ photo_url: string }>(
        `/api/employees/${employeeId}/photo/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        const response = await httpService.post<{ photo_url: string }>(
          `/api/employees/${employeeId}/photo/upload/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return response.data;
      }
      throw e;
    }
  }

  /**
   * Delete employee photo
   * @param employeeId - ID of the employee
   * @returns Promise that resolves when photo is deleted
   */
  static async deleteEmployeePhoto(employeeId: number): Promise<void> {
    try {
      await httpService.delete(`/api/employees/${employeeId}/photo`);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        await httpService.delete(`/api/employees/${employeeId}/photo/`);
        return;
      }
      throw e;
    }
  }
}

export default EmployeeService; 
